var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Device } from "@auvious/compatibility";
import { decodeBase64, encodeBase64 } from "./base64";
class ZSTDCodec {
    constructor() {
        this.format = "zstd";
        this.usesWasm = false;
    }
    get canCompress() {
        return !!this.encodec;
    }
    get canDecompress() {
        return !!this.decodec;
    }
    /** try to load and test zstd library */
    load(useCompress, useDecompress) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.encodec || this.decodec) {
                return;
            }
            // test data
            const data = `1234567890-=~!@#$%^&*()_+qwertyuiop[]\\asdfghjkl;'zxcvbnm,./QWERTYUIOP{}}|ASDFCGHJKL:"ZXSCVBNM<>?€€€`;
            const compressed = `KLUv/SB7fQMAZAYxMjM0NTY3ODkwLT1+IUAjJCVeJiooKV8rcXdlcnR5dWlvcFtdXGFzZGZnaGprbDsnenhjdmJubSwuL1FXRVJUWVVJT1B7fX18QVNERkNHSEpLTDoiWlhTQ1ZCTk08Pj/igqwAAAACAKDP6ODM`;
            // try load both at once
            if (useCompress && Device.isWasmSupported) {
                try {
                    const module = yield import("@oneidentity/zstd-js/wasm");
                    this.encodec = this.decodec = yield module.ZstdInit();
                    // test compression
                    if ((yield this.compressEncoded(data)) !== compressed) {
                        this.encodec = null;
                    }
                    else {
                        this.usesWasm = true;
                    }
                }
                catch (ex) {
                    this.usesWasm = false;
                    this.encodec = null;
                    console.error("Auvious: failed to use zstd for compression:", ex === null || ex === void 0 ? void 0 : ex.message);
                }
            }
            if (useDecompress) {
                if (!this.decodec) {
                    // try loading wasm module for modern browser
                    if (Device.isWasmSupported) {
                        try {
                            const module = yield import("@oneidentity/zstd-js/wasm/decompress");
                            this.decodec = yield module.ZstdInit();
                            this.usesWasm = true;
                        }
                        catch (ex) {
                            this.usesWasm = false;
                            console.error("Auvious: failed to load zstd for decompression:", ex === null || ex === void 0 ? void 0 : ex.message);
                        }
                    }
                    else {
                        // try load optimized js for legacy browser
                        try {
                            const module = yield import("@oneidentity/zstd-js/asm/decompress");
                            this.decodec = yield module.ZstdInit();
                            this.usesWasm = false;
                        }
                        catch (ex) {
                            console.error("Auvious: failed to load zstd for decompression:", ex === null || ex === void 0 ? void 0 : ex.message);
                        }
                    }
                }
                // test decompression
                try {
                    if ((yield this.decompress(compressed)) !== data) {
                        this.decodec = null;
                        this.usesWasm = false;
                    }
                }
                catch (ex) {
                    this.usesWasm = false;
                    this.decodec = null;
                    console.error("Auvious: failed to use zstd for decompression:", ex === null || ex === void 0 ? void 0 : ex.message);
                }
            }
        });
    }
    /** compress a text string and return base64 */
    compressEncoded(data) {
        try {
            return Promise.resolve(this.encodec
                ? encodeBase64(this.encodec.ZstdSimple.compress(new TextEncoder().encode(data), ZSTDCodec.compressionLvl))
                : null);
        }
        catch (ex) {
            return Promise.resolve(null);
        }
    }
    /** compress a text string and return uint8array */
    compress(data) {
        try {
            return Promise.resolve(this.encodec
                ? this.encodec.ZstdSimple.compress(new TextEncoder().encode(data), ZSTDCodec.compressionLvl)
                : null);
        }
        catch (ex) {
            return Promise.resolve(null);
        }
    }
    /** a string in base64 */
    decompress(data) {
        try {
            return Promise.resolve(this.decodec
                ? new TextDecoder().decode(this.decodec.ZstdSimple.decompress(decodeBase64(data)))
                : null);
        }
        catch (ex) {
            console.error(`Auvious: failed to decompress data (${data === null || data === void 0 ? void 0 : data.length} bytes, base64) using zstd:`, ex === null || ex === void 0 ? void 0 : ex.message);
            return Promise.resolve(null);
        }
    }
}
ZSTDCodec.compressionLvl = 2;
export const Zstd = new ZSTDCodec();
